import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
    createNewRetailMatchOverviewMap,
    setRetailMatchOverMapMarkers,
} from "../../../../Actions/ReportMapActions";
import { RenderType } from "../../../../Enums/RenderType";
import { IBrandSummaryData } from "../../../../Models/IBrandSummaryData";
import {
    IRetailMatchJobResult,
    IStoreScoreRecord,
} from "../../../../Models/IRetailMatch";
import { retailMatchMultiBrandDataSelector } from "../../../../Reducers/Selectors/RetailMatchSelectors";
import { getBenchmarkAddressOverride } from "../../../../Util/RetailMatch/RetailMatchUtil";
import {
    ReportInnerWrapper,
    ReportMap,
    ReportScrollableSection,
} from "../../../Common";
import { ReportMapWrapper } from "../../../Common/Map/Styles/MapStyles";
import { formatLongAddressFromRecord } from "../Logic/SharedMethods";
import {
    getBrandSummaryData,
    MAX_BRAND_COUNT_OPTIMIZATION_MODE,
    rankDataSort,
} from "../Util/RetailMatchUtil";
import { createBrandMapMarker } from "./Map/GoogleMapBrandMarker";
import { getMapInfoWindowContentString } from "./Map/MapUtil";
import RetailMatchBrandCard from "./RetailMatchBrandCard";
import RetailMatchHeader from "./RetailMatchHeader/RetailMatchHeader";
import { RetailMatchIntroduction } from "./RetailMatchIntroduction";
interface IRetailMatchMultiBrandProps {
    jobResult: IRetailMatchJobResult;
    benchmarkRecord: IStoreScoreRecord;
    isSharing?: boolean;
    renderType: RenderType;
    onPrintClick: () => void;
    onShareClick: () => void;
}

const mapDomId = "retail-match-overview-map";

const RetailMatchMultiBrand = (props: IRetailMatchMultiBrandProps) => {
    const dispatch = useDispatch();

    const [mapReady, setMapReady] = useState<boolean>(false);

    const {
        map: retailMatchOverviewMap,
        markers: retailMatchOverviewMapMarkers,
        brand: selectedBrand,
    } = useSelector(retailMatchMultiBrandDataSelector);

    const addEventListenersToMarker = (
        marker: any,
        address: string,
        city: string,
        state: string
    ) => {
        const infoWindow = new google.maps.InfoWindow({
            content: getMapInfoWindowContentString(address, city, state),
        });

        marker.addListener("mouseover", () =>
            infoWindow.open(retailMatchOverviewMap, marker)
        );
        marker.addListener("mouseout", () => infoWindow.close());
    };

    const putMarkersOnMap = () => {
        const newMarkers: google.maps.Marker[] = [];

        if (retailMatchOverviewMap && props.jobResult.chartData.storeScores) {
            //Remove all existing markers first
            if (retailMatchOverviewMapMarkers?.length > 0) {
                for (const marker of retailMatchOverviewMapMarkers) {
                    marker.setMap(null);
                }
            }

            //Store markers
            for (const store of props.jobResult.chartData.storeScores) {
                const brand = props.jobResult.job.brands.find((brand) => {
                    return store.bid === brand.brandId;
                });

                if(brand) {
                    const marker = createBrandMapMarker({
                        map: retailMatchOverviewMap,
                        latlng: { lat: store.lat, lng: store.lon },
                        imgSrc: brand.logoPath,
                        animation: google.maps.Animation.DROP,
                        markerId: `rm-brand-marker-${store.sid}`,
                    });
                    addEventListenersToMarker(marker, store.addr, store.city, store.st);
                    newMarkers.push(marker);
                } else {
                    // Handle case where brand is undefined
                    console.error("Brand not found for store:", store);
                }
            }

            //Benchmark marker
            const benchmarkMarker = new google.maps.Marker({
                map: retailMatchOverviewMap,
                draggable: false,
                animation: google.maps.Animation.DROP,
                position: {
                    lat: props.benchmarkRecord.lat,
                    lng: props.benchmarkRecord.lon,
                },
            });
            addEventListenersToMarker(
                benchmarkMarker,
                props.benchmarkRecord.addr,
                props.benchmarkRecord.city,
                props.benchmarkRecord.st
            );
            newMarkers.push(benchmarkMarker);
        }

        dispatch(setRetailMatchOverMapMarkers(newMarkers));
    };

    useEffect(putMarkersOnMap, [
        retailMatchOverviewMap,
        props.jobResult.job.jobId,
        selectedBrand,
    ]);

    const benchmarkAddressOverride: IStoreScoreRecord
        = getBenchmarkAddressOverride(props.jobResult, props.benchmarkRecord);

    const longAddress = formatLongAddressFromRecord(benchmarkAddressOverride);

    //This is so I can maintain the order of brands between the overview section and the cards at the bottom
    //For some reason, the are not in the same order in both places
    const brandCardData: IBrandSummaryData[] = props.jobResult.job.brands.map(
        (brand) => {
            return getBrandSummaryData(brand, props.jobResult);
        }
    );

    const topNBrands = brandCardData
        .sort(rankDataSort)
        .slice(0, MAX_BRAND_COUNT_OPTIMIZATION_MODE);

    const onMapReady = () => {
        setMapReady(true);
    };

    return (
        <ReportInnerWrapper>
            {
                mapReady &&
                <>
                    <RetailMatchHeader
                        multiBrand={true}
                        locationAddress={longAddress}
                        job={props.jobResult.job}
                        isSharing={props.isSharing}
                        onPrintClick={props.onPrintClick}
                        onShareClick={props.onShareClick}
                    />
                    <RetailMatchIntroduction
                        multiBrand={true}
                        brand={null}
                        jobResult={props.jobResult}
                        locationAddress={longAddress}
                        isSharing={props.isSharing}
                        benchmarkAddressOverride={benchmarkAddressOverride}
                        renderType={props.renderType}
                    />
                </>
            }
            <ReportScrollableSection
                style={{
                    display: "inherit",
                    marginBottom: "30px",
                    backgroundColor: "white",
                    height: "700px",
                    paddingTop: "0",
                }}
            >
                <ReportMapWrapper id={`${mapDomId}-container`}>
                    <ReportMap
                        map={retailMatchOverviewMap}
                        mapDomId={mapDomId}
                        createMapAction={createNewRetailMatchOverviewMap}
                        onMapReady={onMapReady}
                    />
                </ReportMapWrapper>
            </ReportScrollableSection>
            <ReportScrollableSection
                style={{
                    display: "inherit",
                    marginBottom: "30px",
                    backgroundColor: "white",
                    overflow: "auto",
                    height: "188px",
                }}
            >
                {topNBrands.map((brandSummary, index) => {
                    return (
                        <div
                            key={brandSummary.brand.brandId}
                            style={{
                                width: "20%",
                                borderLeft: index === 0 ? "none" : "solid 1px #e4e4e4",
                                float: "left",
                            }}
                        >
                            <RetailMatchBrandCard
                                jobId={props.jobResult.job.jobId}
                                rank={index + 1}
                                isSharing={props.isSharing}
                                brandSummary={brandSummary}
                            />
                        </div>
                    );
                })}
            </ReportScrollableSection>
        </ReportInnerWrapper>
    );
};

export default RetailMatchMultiBrand;
